<template>
  <div id="app">
    <PmsShell
      v-if="shellKind === 'pms'"
      :header-nav-items="pmsHeaderNav"
      active-header-id="pms"
      :side-nav-sections="pmsSideNav"
      :active-side-nav-id="pmsActiveId"
      :avatar-initials="avatarInitials"
      :avatar-title="avatarTitle"
      @select-side-nav="onPmsNavSelect"
    >
      <router-view />
    </PmsShell>
    <PortalShell
      v-else-if="shellKind === 'portal'"
      :tabs="portalTabs"
      :active-id="portalActiveId"
      :partner-name="partnerDisplayName"
      :partner-subtitle="partnerSubtitle"
      :avatar-initials="avatarInitials"
      @select-tab="onPortalTabSelect"
      @logout="onLogout"
    >
      <router-view />
    </PortalShell>
    <router-view v-else />
    <SessionExpiredOverlay />
    <UpdateBanner v-if="updateAvailable" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useSessionRefresh } from '@/composables/useSessionRefresh'
import { useVersionCheck } from '@/composables/useVersionCheck'
import { useChunkErrorHandler } from '@/composables/useChunkErrorHandler'
import { useAuthStore } from '@/stores/auth.store'
import { CALCULATOR_ENABLED } from '@/config/features'
import { canViewDashboard } from '@/types/auth.types'
import SessionExpiredOverlay from '@/components/organisms/SessionExpiredOverlay.vue'
import UpdateBanner from '@/components/molecules/UpdateBanner.vue'
import PmsShell from '@/components/library/organisms/PmsShell.vue'
import PortalShell from '@/components/library/organisms/PortalShell.vue'
import type { PmsHeaderNavItem } from '@/components/library/organisms/PmsHeader.vue'
import type {
  PmsSideNavItem,
  PmsSideNavSection,
} from '@/components/library/organisms/PmsSideNav.vue'
import type { PortalHeaderTab } from '@/components/library/organisms/PortalHeader.vue'

useChunkErrorHandler()
const { startListening } = useSessionRefresh()
startListening()
const { updateAvailable, startPolling } = useVersionCheck()
startPolling()

const route = useRoute()
const router = useRouter()
const { t } = useI18n({ useScope: 'global' })
const authStore = useAuthStore()

// ─── Shell selection ────────────────────────────────────────────
//
// Public routes (auth, /dashboard-disabled, error pages for unauth
// visitors) and routes nested under `/` (which carry `MainLayout` from
// the router config — `/account`, `/admin/partner-import`) render
// bare. Only `/pms/*` and `/portal/*` opt in to the new Carbon shells,
// and only when an authenticated user is present — an unauthenticated
// visitor on `/pms/junk` or `/500` gets the bare layout so the styled
// error page can stand on its own.
const shellKind = computed<'pms' | 'portal' | null>(() => {
  if (!authStore.currentUser) return null
  if (route.path.startsWith('/pms')) return 'pms'
  if (route.path.startsWith('/portal')) return 'portal'
  return null
})

// ─── Avatar / partner identity ──────────────────────────────────
const initialsOf = (s: string): string => s.trim().charAt(0).toUpperCase() || ''

const avatarInitials = computed(() => {
  const u = authStore.currentUser
  if (!u) return ''
  const first = initialsOf(u.first_name ?? '')
  const last = initialsOf(u.last_name ?? '')
  if (first || last) return `${first}${last}` || first || last
  return initialsOf(u.email ?? '')
})

const avatarTitle = computed(() => {
  const u = authStore.currentUser
  if (!u) return ''
  const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()
  return full || u.email || ''
})

const partnerDisplayName = computed(() => avatarTitle.value || undefined)
const partnerSubtitle = computed(() =>
  authStore.currentUser?.email ? authStore.currentUser.email : undefined,
)

// ─── PMS nav model ──────────────────────────────────────────────
// Header tabs live in the design bundle's Shell.jsx as ["PMS",
// "Knowledge base", "Supporto"]; only the PMS tab is wired to a
// route. The other two are placeholders kept for visual parity.
//
// Labels are inline Italian (sentence case) instead of i18n keys
// because (a) view titles like `pms.closure.title` carry a `{month}`
// placeholder unsuitable for a nav label, and (b) the design bundle
// itself hardcodes nav strings — they're a presentation config, not
// view content.
const pmsHeaderNav = computed<PmsHeaderNavItem[]>(() => [
  { id: 'pms', label: 'PMS' },
  { id: 'kb', label: 'Knowledge base' },
  { id: 'support', label: 'Supporto' },
])

const PMS_NAV_PATHS: Record<string, string> = {
  dashboard: '/pms/dashboard',
  agenda: '/pms/agenda',
  pipeline: '/pms/pipeline',
  partners: '/pms/partners',
  agencies: '/pms/agencies',
  coupons: '/pms/coupons',
  'blocked-launches': '/pms/blocked-launches',
  'blocked-formats': '/pms/blocked-formats',
  'title-changes': '/pms/title-changes',
  'useful-links': '/pms/useful-links',
  calculator: '/pms/calculator',
  'coupon-new': '/pms/coupons/new',
  'partners-new-bulk': '/pms/partners/new-bulk',
  'agenda-import': '/pms/agenda-import',
  settings: '/pms/settings',
  drive: '/pms/drive',
}

// Pagamenti, Chiusura, TODO, Report canale e Strumento reach non sono nel
// menu, ma le loro route restano raggiungibili da link (Chiusura dalla CTA
// di chiusura mese in Agenda). La Dashboard compare solo ad admin e leader:
// per tutti gli altri `GET /pms/dashboard` risponde 403. Il Calculator è
// nascosto finché `CALCULATOR_ENABLED` è false (vedi `@/config/features`).
const pmsSideNav = computed<PmsSideNavSection[]>(() => [
  {
    label: 'Area di lavoro',
    items: [
      ...(canViewDashboard(authStore.currentUser)
        ? [{ id: 'dashboard', label: 'Dashboard', icon: 'dashboard' } satisfies PmsSideNavItem]
        : []),
      { id: 'agenda', label: 'Agenda', icon: 'calendar' },
      { id: 'pipeline', label: 'Pipeline', icon: 'dashboard' },
      { id: 'partners', label: 'Partner', icon: 'users' },
      { id: 'agencies', label: 'Agenzie', icon: 'inbox' },
      { id: 'coupons', label: 'Coupon', icon: 'tag' },
      { id: 'drive', label: 'Drive', icon: 'doc' },
    ],
  },
  {
    label: 'Operatività',
    items: [
      { id: 'blocked-launches', label: 'Lanci bloccati', icon: 'alert-triangle' },
      { id: 'blocked-formats', label: 'Format bloccati', icon: 'alert-circle' },
      { id: 'title-changes', label: 'Modifica titoli', icon: 'refresh' },
      { id: 'useful-links', label: 'Link utili', icon: 'compass' },
    ],
  },
  {
    label: 'Strumenti',
    items: [
      ...(CALCULATOR_ENABLED
        ? [{ id: 'calculator', label: 'Calculator', icon: 'calculator' } satisfies PmsSideNavItem]
        : []),
      { id: 'coupon-new', label: 'Crea coupon', icon: 'add' },
      { id: 'partners-new-bulk', label: 'Aggiungi potenziali', icon: 'users' },
      { id: 'agenda-import', label: 'Imp. e Exp. Agenda', icon: 'upload' },
      { id: 'settings', label: 'Impostazioni', icon: 'settings' },
    ],
  },
])

// `/pms/partners/:id` activates "partners". Routes with their own sidebar
// entry under a shared first segment — `/pms/coupons/new`,
// `/pms/partners/new-bulk` — are special-cased before the generic
// first-segment-after-`/pms/` rule, which would light up the parent instead.
const pmsActiveId = computed<string | undefined>(() => {
  if (route.path === '/pms/coupons/new') return 'coupon-new'
  if (route.path === '/pms/partners/new-bulk') return 'partners-new-bulk'
  const after = route.path.replace(/^\/pms\/?/, '')
  if (!after) return 'agenda'
  return after.split('/')[0]
})

function onPmsNavSelect(id: string) {
  const target = PMS_NAV_PATHS[id]
  if (target && target !== route.fullPath) router.push(target)
}

// ─── Portal nav model ───────────────────────────────────────────
const portalTabs = computed<PortalHeaderTab[]>(() => [
  { id: 'home', label: t('portal.nav.home') },
  { id: 'coupons', label: t('portal.nav.coupons') },
  { id: 'payments', label: t('portal.nav.payments') },
])

const PORTAL_TAB_PATHS: Record<string, string> = {
  home: '/portal',
  coupons: '/portal/coupons',
  payments: '/portal/payments',
}

const portalActiveId = computed<string | undefined>(() => {
  if (route.path === '/portal') return 'home'
  const after = route.path.replace(/^\/portal\/?/, '')
  if (!after) return 'home'
  return after.split('/')[0]
})

function onPortalTabSelect(id: string) {
  const target = PORTAL_TAB_PATHS[id]
  if (target && target !== route.path) router.push(target)
}

// ─── Portal logout ──────────────────────────────────────────────
// PortalShell re-emits `logout` from the user-menu dropdown (and
// from AccountPanel's danger button). Drop the session and route
// to the new partner login.
async function onLogout() {
  await authStore.logout()
  await router.push({ name: 'login' })
}
</script>

<style lang="scss">
#app {
  min-height: 100vh;
  background-color: var(--cds-background);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
